import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';

interface GlobalLoaderProps {
  onComplete: () => void;
}

export function GlobalLoader({ onComplete }: GlobalLoaderProps) {
  const [visible, setVisible] = useState(true);
  const hourRef = useRef<SVGLineElement>(null);
  const minuteRef = useRef<SVGLineElement>(null);
  const secondRef = useRef<SVGLineElement>(null);
  const glowRef = useRef<SVGCircleElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hourDeg = { val: 0 };
    const minuteDeg = { val: 0 };
    const secondDeg = { val: 0 };

    const setRotation = (el: SVGLineElement | null, deg: number) => {
      if (el) el.setAttribute('transform', `rotate(${deg}, 100, 100)`);
    };

    // Second hand: 1 full rotation per 0.5s (very fast)
    const secondTween = gsap.to(secondDeg, {
      val: 360,
      duration: 0.5,
      ease: 'none',
      repeat: -1,
      onUpdate: () => setRotation(secondRef.current, secondDeg.val),
    });

    // Minute hand: 1 full rotation per 6s
    const minuteTween = gsap.to(minuteDeg, {
      val: 360,
      duration: 6,
      ease: 'none',
      repeat: -1,
      onUpdate: () => setRotation(minuteRef.current, minuteDeg.val),
    });

    // Hour hand: 1 full rotation per 72s (12× slower than minute)
    const hourTween = gsap.to(hourDeg, {
      val: 360,
      duration: 72,
      ease: 'none',
      repeat: -1,
      onUpdate: () => setRotation(hourRef.current, hourDeg.val),
    });

    // Pulse glow on the clock face
    gsap.to(glowRef.current, {
      opacity: 0.25,
      scale: 1.08,
      duration: 1.2,
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true,
      transformOrigin: '50% 50%',
    });

    // Auto-dismiss after 2.6s
    const timer = setTimeout(() => {
      secondTween.kill();
      minuteTween.kill();
      hourTween.kill();

      gsap.to(containerRef.current, {
        opacity: 0,
        scale: 1.06,
        duration: 0.7,
        ease: 'power2.inOut',
        onComplete: () => {
          setVisible(false);
          onComplete();
        },
      });
    }, 2600);

    return () => {
      clearTimeout(timer);
      secondTween.kill();
      minuteTween.kill();
      hourTween.kill();
    };
  }, [onComplete]);

  if (!visible) return null;

  const markers = Array.from({ length: 12 }, (_, i) => {
    const angle = (i * 30 * Math.PI) / 180;
    const isMajor = i % 3 === 0;
    const r1 = isMajor ? 78 : 80;
    const r2 = isMajor ? 88 : 85;
    return {
      x1: 100 + r1 * Math.sin(angle),
      y1: 100 - r1 * Math.cos(angle),
      x2: 100 + r2 * Math.sin(angle),
      y2: 100 - r2 * Math.cos(angle),
      major: isMajor,
    };
  });

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#050505',
      }}
    >
      {/* Ambient gold glow */}
      <div
        style={{
          position: 'absolute',
          width: 360,
          height: 360,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0.04) 60%, transparent 80%)',
          filter: 'blur(40px)',
          pointerEvents: 'none',
        }}
      />

      {/* Clock */}
      <motion.div
        initial={{ opacity: 0, scale: 0.82 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Outer bezel */}
          <circle cx="100" cy="100" r="97" stroke="url(#bezelGradient)" strokeWidth="2.5" fill="none" />
          {/* Face */}
          <circle cx="100" cy="100" r="93" fill="url(#faceGradient)" />
          {/* Inner accent ring */}
          <circle cx="100" cy="100" r="88" stroke="rgba(212,175,55,0.15)" strokeWidth="0.8" fill="none" />
          {/* Glow pulse */}
          <circle ref={glowRef} cx="100" cy="100" r="70" fill="url(#glowGradient)" opacity="0.15" />

          {/* Hour markers */}
          {markers.map((m, i) => (
            <line
              key={i}
              x1={m.x1} y1={m.y1} x2={m.x2} y2={m.y2}
              stroke={m.major ? '#D4AF37' : 'rgba(212,175,55,0.45)'}
              strokeWidth={m.major ? 2 : 1}
              strokeLinecap="round"
            />
          ))}

          {/* Brand name on dial */}
          <text x="100" y="70" textAnchor="middle" fill="rgba(212,175,55,0.9)" fontSize="7"
            fontFamily="'Cormorant Garamond', serif" letterSpacing="2">
            SARDARTIMES94
          </text>
          <text x="100" y="130" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="5"
            fontFamily="Inter, sans-serif" letterSpacing="1.5">
            EST. 1994
          </text>

          {/* Hour hand — gold */}
          <line ref={hourRef} x1="100" y1="100" x2="100" y2="48"
            stroke="#D4AF37" strokeWidth="3.5" strokeLinecap="round"
            style={{ filter: 'drop-shadow(0 0 3px #D4AF37)' }} />

          {/* Minute hand — white */}
          <line ref={minuteRef} x1="100" y1="100" x2="100" y2="34"
            stroke="#ffffff" strokeWidth="2" strokeLinecap="round"
            style={{ filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.6))' }} />

          {/* Second hand — orange, extends behind center */}
          <line ref={secondRef} x1="100" y1="114" x2="100" y2="28"
            stroke="#ff6b35" strokeWidth="1" strokeLinecap="round"
            style={{ filter: 'drop-shadow(0 0 2px #ff6b35)' }} />

          {/* Center cap */}
          <circle cx="100" cy="100" r="4.5" fill="#D4AF37" />
          <circle cx="100" cy="100" r="2" fill="#050505" />

          <defs>
            <radialGradient id="faceGradient" cx="40%" cy="35%" r="70%">
              <stop offset="0%" stopColor="#1a1208" />
              <stop offset="60%" stopColor="#0d0d0d" />
              <stop offset="100%" stopColor="#050505" />
            </radialGradient>
            <linearGradient id="bezelGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#F0D060" />
              <stop offset="25%" stopColor="#D4AF37" />
              <stop offset="50%" stopColor="#A8892A" />
              <stop offset="75%" stopColor="#D4AF37" />
              <stop offset="100%" stopColor="#F0D060" />
            </linearGradient>
            <radialGradient id="glowGradient" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>
      </motion.div>

      {/* Brand label */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        style={{ marginTop: 32, textAlign: 'center' }}
      >
        <p style={{ fontFamily: "'Cormorant Garamond', serif", color: '#D4AF37', letterSpacing: '0.3em', fontSize: 18, textTransform: 'uppercase' }}>
          SardarTimes94
        </p>
        <p style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif', fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', marginTop: 4 }}>
          Authorized Luxury Retailer
        </p>
      </motion.div>

      {/* Gold progress line sweeping from center outward */}
      <motion.div
        style={{
          position: 'absolute',
          bottom: 0,
          left: '50%',
          height: 2,
          background: 'linear-gradient(90deg, transparent, #D4AF37, transparent)',
        }}
        initial={{ width: '0%', translateX: '-50%' }}
        animate={{ width: '100%', translateX: '-50%' }}
        transition={{ duration: 2.4, ease: 'easeInOut' }}
      />
    </div>
  );
}